import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ArrowUp, ArrowDown, ArrowLeft, Trash2, Gavel } from "lucide-react";
import { toast } from "sonner";

export default function Thread() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [err, setErr] = useState("");

  const load = () => api.get(`/threads/${threadId}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [threadId]);

  const canModerate = user?.role === "admin" || user?.role === "moderator";

  const vote = async (target, id, value) => {
    if (!user) return nav("/login");
    try {
      await api.post(`/${target}/${id}/vote`, { value });
      load();
    } catch {}
  };

  const submitReply = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/posts", { thread_id: threadId, content: reply });
      setReply("");
      load();
    } catch (e) { setErr(formatError(e.response?.data?.detail) || e.message); }
  };

  const deleteThread = async () => {
    if (!window.confirm(canModerate && user?.id !== data.thread.author_id ? "Remove this thread as moderator?" : "Delete this thread?")) return;
    try {
      await api.delete(`/threads/${threadId}`);
      toast.success("Thread removed");
      nav("/");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const deletePost = async (postId, byAuthor) => {
    if (!window.confirm(byAuthor ? "Delete your reply?" : "Remove this reply as moderator?")) return;
    try {
      await api.delete(`/posts/${postId}`);
      toast.success("Reply removed");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (!data) return <div className="p-8" data-testid="thread-loading">Loading...</div>;
  const { thread, posts } = data;
  const myVote = thread.voters?.[user?.id] || 0;
  const canDeleteThread = user && (user.id === thread.author_id || canModerate);

  return (
    <div className="pastel-grain min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link to={`/category/${thread.category_id}`} className="inline-flex items-center gap-2 text-sm font-bold mb-4" data-testid="thread-back-link">
          <ArrowLeft size={16} /> Back to {thread.category_name}
        </Link>

        <article className="brutal-border brutal-shadow rounded-2xl p-6 sm:p-8 bg-white mb-6">
          <div className="sticker mb-4" style={{ background: "var(--secondary)" }}>{thread.category_name}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">{thread.title}</h1>
          <div className="text-xs text-zinc-500 mt-2 font-medium">
            by{" "}
            <Link to={`/profile/${thread.author_id}`} className="font-bold text-zinc-800 underline" data-testid="thread-author-link">
              {thread.author_name}
            </Link>
          </div>
          <div className="mt-5 prose font-medium text-zinc-800 whitespace-pre-wrap" data-testid="thread-content">
            {thread.content}
          </div>
          <div className="mt-6 flex items-center gap-2">
            <VoteButtons votes={thread.votes} myVote={myVote} onVote={(v) => vote("threads", thread.id, v)} testId={`vote-thread-${thread.id}`} />
            {canDeleteThread && (
              <Button onClick={deleteThread} data-testid="delete-thread-btn"
                className="ml-auto h-9 brutal-border brutal-shadow-sm rounded-lg font-bold gap-2 bg-white hover:bg-white text-[var(--destructive)]">
                {canModerate && user?.id !== thread.author_id ? <Gavel size={14} /> : <Trash2 size={14} />}
                {canModerate && user?.id !== thread.author_id ? "Remove" : "Delete"}
              </Button>
            )}
          </div>
        </article>

        <h2 className="font-display text-xl font-bold mb-3">{posts.length} replies</h2>
        <div className="space-y-3 mb-6">
          {posts.map((p, i) => {
            const pv = p.voters?.[user?.id] || 0;
            const canDeletePost = user && (user.id === p.author_id || canModerate);
            const byAuthor = user?.id === p.author_id;
            return (
              <div key={p.id} data-testid={`post-${p.id}`}
                className="brutal-border brutal-shadow-sm rounded-xl p-5 bg-white fade-up"
                style={{ animationDelay: `${i * 30}ms` }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="text-xs text-zinc-500 font-medium">
                    <Link to={`/profile/${p.author_id}`} className="font-bold text-zinc-800">{p.author_name}</Link>
                  </div>
                  {canDeletePost && (
                    <button onClick={() => deletePost(p.id, byAuthor)}
                      data-testid={`delete-post-${p.id}`}
                      className="text-xs font-bold brutal-border rounded-lg px-2 py-1 bg-white hover:bg-red-50 text-[var(--destructive)] inline-flex items-center gap-1">
                      {canModerate && !byAuthor ? <><Gavel size={12} /> Remove</> : <><Trash2 size={12} /> Delete</>}
                    </button>
                  )}
                </div>
                <p className="mt-2 font-medium text-zinc-800 whitespace-pre-wrap">{p.content}</p>
                <div className="mt-3">
                  <VoteButtons votes={p.votes} myVote={pv} onVote={(v) => vote("posts", p.id, v)} testId={`vote-post-${p.id}`} />
                </div>
              </div>
            );
          })}
        </div>

        {user ? (
          <form onSubmit={submitReply} data-testid="reply-form"
            className="brutal-border brutal-shadow rounded-xl p-5 bg-white space-y-3">
            <h3 className="font-display font-bold">Reply</h3>
            <Textarea data-testid="reply-input" value={reply} onChange={(e) => setReply(e.target.value)}
              placeholder="Write your reply..." rows={4} required
              className="brutal-border rounded-xl font-medium focus-visible:ring-0 focus-visible:border-[var(--ink)]" />
            {err && <div className="text-sm font-bold text-[var(--destructive)]">{err}</div>}
            <Button type="submit" data-testid="reply-submit-btn"
              className="brutal-border brutal-shadow rounded-xl font-bold"
              style={{ background: "var(--primary)", color: "var(--ink)" }}>
              Send reply
            </Button>
          </form>
        ) : (
          <div className="brutal-border brutal-shadow-sm rounded-xl p-5 bg-white text-center">
            <Link to="/login" className="font-bold underline" data-testid="reply-login-prompt">Log in</Link> to reply.
          </div>
        )}
      </div>
    </div>
  );
}

function VoteButtons({ votes, myVote, onVote, testId }) {
  return (
    <div className="inline-flex items-center gap-1 brutal-border rounded-full p-1 bg-white">
      <button onClick={() => onVote(1)} data-testid={`${testId}-up`}
        className={`w-8 h-8 grid place-items-center rounded-full ${myVote === 1 ? "bg-[var(--primary)]" : ""}`}>
        <ArrowUp size={16} strokeWidth={2.5} />
      </button>
      <span className="font-display font-black px-2 min-w-[24px] text-center" data-testid={`${testId}-count`}>{votes}</span>
      <button onClick={() => onVote(-1)} data-testid={`${testId}-down`}
        className={`w-8 h-8 grid place-items-center rounded-full ${myVote === -1 ? "bg-[var(--destructive)] text-white" : ""}`}>
        <ArrowDown size={16} strokeWidth={2.5} />
      </button>
    </div>
  );
}
